<template>
  <div id="churches">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="churches-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <v-icon class="churches-icon" :icon="'mdi-church'" />
              CHURCHES
            </h1>
          </div>
          <v-flex class="d-flex align-center mb-7 mt-12">
            <div v-if="checkAuthorization(['superAdmin'])" class="mr-6">
              <v-btn
                v-if="!this.openChurchForm"
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold"
                @click="addNewHandler"
              >
                CREATE NEW CHURCH
              </v-btn>
              <div v-else class="close-link" @click="closebuilder">
                <v-icon :icon="'arrowleft'" />
                <span class="close-link-text">{{
                  $t('common.builderClose')
                }}</span>
              </div>
            </div>
            <span v-if="churches" class="churches-action-bar font-weight-bold"
              >TOTAL CHURCHES: {{ visibleChurches.length }}</span
            >
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              :append-inner-icon="search ? '' : 'mdi-magnify'"
              label="Search"
              single-line
              hide-details
              clearable
              variant="solo"
              flat
              density="compact"
              class="search-input pb-0 pr-3"
              color="#E8E9EC"
              style="max-width: 265px"
            ></v-text-field>
          </v-flex>
          <v-flex class="text-caption text-uppercase d-flex mb-3">
            <v-btn
              size="small"
              variant="text"
              min-width="inherit"
              height="inherit"
              disabled
              tile
              class="pl-0 pr-1 filter-btn"
              >Filter:</v-btn
            >
            <v-btn
              size="small"
              variant="text"
              min-width="inherit"
              height="inherit"
              tile
              class="font-weight-bold pa-0 sort-btn"
              @click="churchFilters('show-archived')"
              >{{
                areArchivedChurchesVisible ? 'Hide' : 'Show'
              }}
              Archived</v-btn
            >
            <div class="d-flex px-2 ml-auto">
              <v-menu location="top left" :close-on-content-click="false">
                <template #activator="{ props }">
                  <v-icon v-bind="props" size="24">mdi-view-column</v-icon>
                </template>
                <v-list class="header-menu-list">
                  <v-list-item v-for="(h, i) in headers" :key="i">
                    <v-checkbox
                      v-model="selectedHeaders"
                      :value="h"
                      :label="h.title"
                      density="compact"
                      color="#A5D867"
                      hide-details
                      multiple
                      @update:model-value="setShowHeaders"
                    >
                    </v-checkbox>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-flex>
          <v-form
            v-model="isFormValid"
            v-if="openChurchForm"
            class="mb-12"
            ref="addeditform"
          >
            <v-card class="py-10 px-15">
              <v-card-title class="px-0 font-weight-bold font-size-32">{{
                editingChurch ? 'EDIT CHURCH' : 'CHURCH BUILDER'
              }}</v-card-title>
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >Church Name</v-card-text
              >
              <v-text-field
                v-model="churchTitle"
                variant="outlined"
                placeholder="Enter the church name"
                :rules="[rules.required, rules.length(3)]"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>

              <v-card-text
                class="pa-0 pt-2 mb-1 text-subtitle-1 font-weight-bold text-uppercase"
                >Admin</v-card-text
              >
              <v-text-field
                v-model="churchAdmin"
                variant="outlined"
                placeholder="Enter the church admin's name"
                :rules="[rules.required, rules.length(2)]"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>

              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >Admin's Email</v-card-text
              >
              <v-text-field
                v-model="churchAdminEmail"
                variant="outlined"
                placeholder="Enter the church admin's email"
                :rules="emailRule"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>

              <v-card-text
                class="pa-0 pt-4 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('churchLocale.adminLabel') }}</v-card-text
              >
              <v-select
                v-model="churchLocale"
                :items="churchLocaleOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :placeholder="$t('churchLocale.adminPlaceholder')"
                :hint="$t('churchLocale.adminHint')"
                persistent-hint
                clearable
                class="user-input mt-1"
                color="#A0C548"
                style="min-width: 100%"
              ></v-select>
            </v-card>
            <div class="text-center">
              <v-btn
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold my-14 px-14"
                :disabled="!isFormValid"
                @click="editingChurch ? editChurch() : addChurch()"
                >{{ editingChurch ? 'EDIT' : 'ADD' }}</v-btn
              >
            </div>
            <v-progress-linear
              :indeterminate="iseditingChurch"
              :active="iseditingChurch"
              color="#592892"
            ></v-progress-linear>
            <v-alert
              v-if="feedback"
              border="start"
              color="#592892"
              class="text-center"
            >
              {{ feedback }}
            </v-alert>
            <v-divider class="dashed"></v-divider>
          </v-form>
          <v-data-table
            :headers="showHeaders"
            fixed-header
            :loading="isloading"
            loading-text="Loading Churches... Please wait"
            no-data-text="No Churches found"
            :items="visibleChurches"
            :items-per-page="20"
            :header-props="{ sortIcon: '$sortarrowdown' }"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, -1],
            }"
            v-model:expanded="expanded"
            expand-icon="$edit"
            item-key="email"
            :row-props="(item) => ({ class: row_classes(item) })"
            :single-expand="singleExpand"
            :search="search"
            class="elevation-1 churches-table"
          >
            <template #progress>
              <v-progress-linear color="#592892" indeterminate />
            </template>

            <template #item.churchtitle="{ item }">
              {{ item.churchtitle }}
            </template>

            <template #item.cid="{ item }">
              {{ item.cid }}
            </template>

            <template #item.groups="{ item }">
              <div v-if="item.cid" :class="item.cid">
                {{ lookUpGroups(item.cid).length }}
              </div>
            </template>

            <template #item.churchadmin="{ item }">
              {{ item.churchadmin }}
            </template>

            <template #item.churchadminemail="{ item }">
              {{ item.churchadminemail }}
            </template>

            <template #item.churchLocale="{ item }">
              {{ formatChurchLocale(item.churchLocale) }}
            </template>

            <template #item.subscriptionDate="{ item }">
              <div v-if="item.subscriptionDate">
                {{ formatDate(item.subscriptionDate) }}
              </div>
            </template>

            <template
              #item.actions="{ item }"
              v-if="isAuthenticated && checkAuthorization(['superAdmin'])"
            >
              <div class="action-icons d-flex align-center justify-end">
                <v-icon
                  @click="loadChurchToEdit(item)"
                  :disabled="item.archive"
                  class="mr-6"
                  :icon="'edit'"
                />
                <span v-if="!item.archive">
                  <v-tooltip location="bottom">
                    <template #activator="{ props }">
                      <span v-bind="props">
                        <v-icon
                          @click="archiveChurch(item)"
                          class="archive-icon"
                          :icon="'logout'"
                        />
                      </span>
                    </template>
                    <span>Archive Church</span>
                  </v-tooltip>
                </span>
                <span v-else>
                  <v-tooltip location="bottom">
                    <template #activator="{ props }">
                      <span v-bind="props">
                        <v-icon
                          @click="activateChurch(item)"
                          class="activate-icon"
                          :icon="'logout'"
                        />
                      </span>
                    </template>
                    <span>Activate Church</span>
                  </v-tooltip>
                </span>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapGetters } from 'vuex'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import { formatDate as globalFormatDate } from '@/components/utilities/formatters'
  import { currentTheme } from '@/plugins/vuetify'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'Churches',
    data() {
      return {
        feedback: null,
        churches: [],
        groups: [],
        isloading: true,
        isFormValid: false,
        expanded: [],
        confirmArchiveDialog: false,
        singleExpand: true,
        showedit: false,
        isediting: false,
        openChurchForm: false,
        editingChurch: false,
        iseditingChurch: false,
        search: '',
        churchTitle: '',
        churchAdmin: '',
        churchAdminEmail: '',
        churchLocale: null,
        churchToEdit: null,
        selectedHeaders: [],
        areArchivedChurchesVisible: false,
        headers: [
          { title: 'Name', default: true, key: 'churchtitle' },
          { title: 'CID', default: true, sortable: false, key: 'cid' },
          {
            title: 'Groups',
            default: true,
            sortable: false,
            filterable: false,
            key: 'groups',
          },
          {
            title: 'Admin',
            default: true,
            sortable: false,
            key: 'churchadmin',
          },
          {
            title: 'Email',
            default: true,
            sortable: false,
            key: 'churchadminemail',
          },
          {
            title: 'Market',
            default: true,
            sortable: true,
            key: 'churchLocale',
          },
          {
            title: 'Subscription',
            default: true,
            sortable: false,
            key: 'subscriptionDate',
          },
          {
            title: 'Actions',
            default: true,
            sortable: false,
            filterable: false,
            key: 'actions',
          },
        ],

        rules: {
          length: (len) => (v) =>
            (v || '').length >= len || `min ${len} characters required `,
          required: (v) => !!v || 'This field is required',
        },
        emailRule: [
          (value) => !!value || 'Required.',
          (value) => (value || '').length <= 40 || 'Max 40 characters',
          (value) => {
            const pattern =
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            return pattern.test(value) || 'Invalid e-mail.'
          },
        ],
        dateFormatConfig: {
          dayOfWeekNames: [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ],
          dayOfWeekNamesShort: ['Su', 'Mo', 'Tu', 'We', 'Tr', 'Fr', 'Sa'],
          monthNames: [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ],
          monthNamesShort: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ],
          // Timezone offset, in minutes (0 - UTC, 180 - Russia, undefined - current)
          timezone: 0,
        },
      }
    },
    components: {
      HowToUseThisPage,
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      editorBackgroundColor() {
        return currentTheme.value === 'dark' ? '#272727' : '#ffffff'
      },
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      showHeaders() {
        return this.headers.filter((h) =>
          this.selectedHeaders.find((s) => s.key === h.key)
        )
      },
      visibleChurches() {
        let allChurches
        if (this.areArchivedChurchesVisible) {
          allChurches = this.churches.filter((c) => c.archive)
        } else {
          allChurches = this.churches.filter((c) => !c.archive)
        }
        return allChurches
        // return this.areArchivedChurchesVisible ? this.churches.filter((c) => c.archive) : this.churches
      },
      churchLocaleOptions() {
        return [
          {
            value: 'en',
            label: this.$t('churchLocale.english'),
          },
          {
            value: 'pt',
            label: this.$t('churchLocale.portuguese'),
          },
        ]
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler(event) {
          this.init(event)
        },
      },
    },
    methods: {
      formatChurchLocale(locale) {
        if (locale === 'en') return this.$t('churchLocale.english')
        if (locale === 'pt') return this.$t('churchLocale.portuguese')
        return this.$t('churchLocale.unset')
      },
      buildChurchLocaleFields() {
        if (!this.churchLocale) {
          return {
            churchLocale: null,
            churchLocaleSetAt: null,
          }
        }
        return {
          churchLocale: this.churchLocale,
          churchLocaleSetAt: Date.now(),
        }
      },
      formatDate(timestamp) {
        return globalFormatDate(timestamp)
      },
      setShowHeaders() {
        localStorage.setItem(
          'setChurchesHeaders',
          JSON.stringify(this.selectedHeaders)
        )
      },
      addNewHandler() {
        this.churchTitle = ''
        this.churchAdmin = ''
        this.churchAdminEmail = ''
        this.churchLocale = null
        if (this.editingChurch) {
          if (this.$refs.addeditform) this.$refs.addeditform.reset()
          this.openChurchForm = true
        } else {
          this.openChurchForm = !this.openChurchForm
        }
        this.editingChurch = false
      },
      generateChurchPin() {
        let text = ''
        let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
        for (let i = 0; i < 4; i++) {
          text += possible.charAt(Math.floor(Math.random() * possible.length))
        }
        let newgid = text
        let newGidToString = newgid.toString()
        return newGidToString
      },
      async addChurch() {
        this.editingChurch = true

        if (this.churchTitle && this.churchAdmin && this.churchAdminEmail) {
          let churchPin
          let newCID
          do {
            churchPin = this.generateChurchPin()
            let churchSnapshot = await db
              .collection('churches')
              .where('cid', '==', churchPin)
              .get()
            if (churchSnapshot.empty) {
              newCID = churchPin
            }
          } while (!newCID)

          if (newCID) {
            db.collection('churches')
              .add({
                active: true,
                archive: false,
                churchtitle: this.churchTitle,
                churchadmin: this.churchAdmin,
                // Emails are case-insensitive; sign-in matches the lowercase form.
                churchadminemail: this.churchAdminEmail.trim().toLowerCase(),
                cid: newCID,
                created: Date.now(),
                subscriptionDate: Date.now(),
                verified: true,
                ...this.buildChurchLocaleFields(),
              })
              .then(() => {
                // Reset fields
                this.churchTitle = ''
                this.churchAdmin = ''
                this.churchAdminEmail = ''
                this.churchLocale = null
                this.openChurchForm = false
                this.editingChurch = false

                // re-init the church list
                this.init()
              })
          }
        } else {
          this.feedback = 'All fields are required'
        }
      },
      churchFilters(which) {
        if (which == 'show-archived') {
          this.areArchivedChurchesVisible = !this.areArchivedChurchesVisible
        }
      },
      closebuilder() {
        this.churchTitle = ''
        this.churchAdmin = ''
        this.churchAdminEmail = ''
        this.churchLocale = null
        // if(this.editingChurch) {
        //     if(this.$refs.addeditform)
        //         this.$refs.addeditform.reset()
        // }
        this.openChurchForm = false
        this.editingChurch = false
      },
      loadChurchToEdit(church) {
        // console.log(church)
        // check to see if this church is already being edited and if so close the edit panel
        if (
          this.editingChurch &&
          this.churchToEdit &&
          this.churchToEdit.name == church.name
        ) {
          this.openChurchForm = false
          this.editingChurch = false
        } else {
          this.editingChurch = true
          this.churchToEdit = church
          this.openChurchForm = true

          this.churchTitle = church.churchtitle
          this.churchAdmin = church.churchadmin
          this.churchAdminEmail = church.churchadminemail
          this.churchLocale = church.churchLocale || null

          this.scrollToTop()
        }
      },
      editChurch() {
        // console.log(church)
        // this.isediting = true

        // // Set a timeout for the user experience
        // setTimeout(() => {
        //     db.collection('churches').doc(church.id).update({
        //         churchtitle: church.churchtitle,
        //     }).then(() => {
        //         this.isediting = false
        //     })
        // }, 1000);
        this.iseditingChurch = true

        if (this.churchTitle && this.churchAdmin && this.churchAdminEmail) {
          setTimeout(() => {
            db.collection('churches')
              .doc(this.churchToEdit.id)
              .update({
                churchtitle: this.churchTitle,
                churchadmin: this.churchAdmin,
                churchadminemail: this.churchAdminEmail.trim().toLowerCase(),
                ...this.buildChurchLocaleFields(),
              })
              .then(() => {
                // Reset fields
                this.churchTitle = ''
                this.churchAdmin = ''
                this.churchAdminEmail = ''
                this.churchLocale = null

                this.churchToEdit = null
                this.openChurchForm = false
                this.editingChurch = false

                // re-init the church list
                this.init()
              })
          }, 1000)
        } else {
          this.feedback = 'There was an error. '

          if (!this.churchTitle)
            this.feedback += 'You are missing a church title. '
          if (!this.churchToEdit)
            this.feedback += 'There was no church to edit. '
        }
      },
      async archiveChurch(church) {
        const isDark = this.$isDarkTheme()

        if (church) {
          const confirmed = await confirmAction({
            title: `You are about to archive: <br>"${church.churchtitle}"`,
            html: 'Type ARCHIVE below to confirm.',
            input: 'text',
            isDark,
            inputPlaceholder: 'Type ARCHIVE',
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (matchesConfirmWord(value, 'ARCHIVE')) {
                  resolve()
                } else {
                  resolve('You need to type ARCHIVE to confirm')
                }
              })
            },
            confirmButtonText: 'Yes Archive it!',
            cancelButtonText: 'No, Keep it!',
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (confirmed) {
            await db
              .collection('churches')
              .doc(church.id)
              .update({ archive: true })
            this.init()
          }
        } else {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the archive function, missing params.',
            icon: 'error',
            isDark,
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      async activateChurch(church) {
        const isDark = this.$isDarkTheme()

        if (church) {
          const confirmed = await confirmAction({
            title: `You are about to activate: <br>"${church.churchtitle}"`,
            html: 'Type ACTIVATE below to confirm.',
            input: 'text',
            isDark,
            inputPlaceholder: 'Type ACTIVATE',
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (matchesConfirmWord(value, 'ACTIVATE')) {
                  resolve()
                } else {
                  resolve('You need to type ACTIVATE to confirm')
                }
              })
            },
            confirmButtonText: 'Yes Activate it!',
            cancelButtonText: 'No, Keep it!',
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (confirmed) {
            await db
              .collection('churches')
              .doc(church.id)
              .update({ archive: false })
            this.init()
          }
        } else {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the archive function, missing params.',
            icon: 'error',
            isDark,
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const hasRole = permissions.filter(() =>
            userRoles.some((role) => permissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      lookUpGroups(cid) {
        return this.groups.filter((item) => item.cid === cid)
      },
      CalDate(date1, date2) {
        let diff = Math.floor(date1.getTime() - date2.getTime())
        let secs = Math.floor(diff / 1000)
        let mins = Math.floor(secs / 60)
        let hours = Math.floor(mins / 60)
        let days = Math.floor(hours / 24)
        let months = Math.floor(days / 31)
        var years = Math.floor(months / 12)
        months = Math.floor(months % 12)
        days = Math.floor(days % 31)
        hours = Math.floor(hours % 24)
        mins = Math.floor(mins % 60)
        secs = Math.floor(secs % 60)
        let message = ''
        // if(days > 0) {
        //     console.log('3 ', months)
        //     if(months >= 12)
        //         subscriptionclass = 'subscription-alert'
        //     if(months > 9)
        //         subscriptionclass = 'subscription-warning'
        // }
        // if(days<=0){
        //     message += secs + " sec ";
        //     message += mins + " min ";
        //     message += hours + " hours ";
        // }else{
        // message += days + " days ";
        if (months > 9 || years > 0) {
          // message += months + " months ";
          message = 'subscription-warning'
        }
        if (years > 0) {
          // message += years + " years ago";
          message = 'subscription-alert'
        }
        // }
        return message
      },
      row_classes(item) {
        return item.archive ? 'disabled' : ''
      },
      scrollToTop() {
        document
          .getElementById('churches')
          .scrollIntoView({ behavior: 'smooth' })
      },
      init() {
        this.churches = []
        this.isloading = true
        this.groups = []

        // we set a timeout for the User experience
        setTimeout(() => {
          let usersRef = db.collection('churches')
          // usersRef = usersRef.where('archive', '==', false)
          usersRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                let church = doc.data()
                church.id = doc.id
                this.churches.push(church)

                // this.churches.sort((a, b) => (a.archive < b.archive) ? -1 : 0)
              })
            })
            .then(() => {
              let groupsRef = db.collection('groups')
              groupsRef
                .get()
                .then((snapshot) => {
                  snapshot.forEach((doc) => {
                    let group = doc.data()
                    group.group_doc_id = doc.id
                    this.groups.push(group)
                  })
                })
                .then(() => {
                  this.isloading = false
                })
            })
        }, 1000)
      },
    },
    created() {
      const storedHeaders = localStorage.getItem('setChurchesHeaders')
      if (storedHeaders) {
        const newDefaults = JSON.parse(storedHeaders)
        const defaultFilter = newDefaults.map(
          (itemY) => itemY.key || itemY.value
        )
        const filtered = this.headers.filter((item) =>
          defaultFilter.includes(item.key)
        )
        this.selectedHeaders = filtered
      } else {
        this.selectedHeaders = this.headers.filter((s) => s.default)
      }
      this.init()
    },
  }
</script>

<style>
  .churches-container {
    margin-bottom: 25%;
  }
  .churches-container .section-heading h1 {
    display: flex;
    align-items: center;
  }
  .churches-container .churches-icon {
    margin-right: 10px;
  }
  .churches-container .churches-action-bar {
    font-size: 15px;
  }
  .v-select .v-select__slot .v-select__selections input[readonly='readonly'] {
    display: none;
  }
  .churches-container .v-input {
    font-size: 0.875rem;
  }
  .churches-container .activate-icon {
    transform: rotate(90deg);
  }
  .churches-container .archive-icon {
    transform: rotate(-90deg);
  }
  .churches-container .archive-icon:focus,
  .churches-container .activate-icon:focus {
    outline: none;
  }
  tr.subscription-alert {
    background-color: rgb(255, 0, 0, 0.15);
  }
  body
    .theme--light.v-data-table
    > .v-data-table__wrapper
    > table
    > tbody
    > tr.subscription-alert:hover:not(.v-data-table__expanded__content):not(
      .v-data-table__empty-wrapper
    ) {
    background-color: rgb(255, 0, 0, 0.25);
  }
  tr.subscription-warning {
    background-color: rgb(255, 193, 7, 0.15);
  }
  body
    .theme--light.v-data-table
    > .v-data-table__wrapper
    > table
    > tbody
    > tr.subscription-warning:hover:not(.v-data-table__expanded__content):not(
      .v-data-table__empty-wrapper
    ) {
    background-color: rgb(255, 193, 7, 0.25);
  }
</style>
